import { EventEmitter, Injectable, OnDestroy } from '@angular/core';
import {
  applyContinuousFocus,
  describeCapabilities,
  CameraCapabilitiesInfo,
} from './camera-focus';

/** Everything obtainable about the camera at stream-start, for analytics. */
export interface CameraStartInfo {
  deviceLabel: string;
  facingMode: string | null;
  width: number | null;
  height: number | null;
  frameRate: number | null;
  aspectRatio: number | null;
  videoInputCount: number;
  capabilities: CameraCapabilitiesInfo;
  continuousFocusApplied: boolean;
}

const HIRES = { width: { ideal: 1920 }, height: { ideal: 1080 } };

/**
 * Owns a single camera MediaStream for the lifetime of one capture flow.
 *
 * Provided at the `full-capture` component level (NOT root), so its lifetime is
 * the flow: when the flow component is destroyed Angular calls {@link ngOnDestroy}
 * → {@link release}, stopping the tracks. Consumers (`docpic`, `qr-capture`)
 * acquire the shared stream and MUST NOT stop its tracks — only detach.
 */
@Injectable()
export class CameraStreamService implements OnDestroy {
  private stream?: MediaStream;
  private key?: string; // deviceId|facingMode of the live stream
  private startedEmitted = false;

  /** Fires once per flow, when the stream is first acquired. */
  readonly started = new EventEmitter<CameraStartInfo>();

  async acquire(opts?: { deviceId?: string; facingMode?: 'environment' | 'user' }): Promise<MediaStream> {
    const deviceId = opts?.deviceId;
    const facingMode = opts?.facingMode ?? 'environment';
    const key = `${deviceId ?? ''}|${facingMode}`;
    if (this.stream && this.key === key && this.stream.getVideoTracks().some((t) => t.readyState === 'live')) {
      return this.stream;
    }
    // Different constraints requested while a stream is live → replace it.
    if (this.stream) this.stopStream();

    const constraints: MediaStreamConstraints = {
      video: deviceId ? { deviceId: { exact: deviceId }, ...HIRES } : { facingMode, ...HIRES },
      audio: false,
    };
    this.stream = await navigator.mediaDevices.getUserMedia(constraints);
    this.key = key;

    const track = this.stream.getVideoTracks()[0];
    const continuousFocusApplied = track ? await applyContinuousFocus(track) : false;

    if (!this.startedEmitted) {
      this.startedEmitted = true;
      this.started.emit(await this.buildStartInfo(track, continuousFocusApplied));
    }
    return this.stream;
  }

  current(): MediaStream | undefined {
    return this.stream;
  }

  videoTrack(): MediaStreamTrack | undefined {
    return this.stream?.getVideoTracks()[0];
  }

  /** Camera picker: swap to a specific device. Does not re-emit `started`. */
  async switchTo(deviceId: string): Promise<MediaStream> {
    this.stopStream();
    return this.acquire({ deviceId });
  }

  /** Stop all tracks and forget the stream. The ONLY place tracks are stopped. */
  release(): void {
    this.stopStream();
  }

  ngOnDestroy(): void {
    this.release();
  }

  private stopStream(): void {
    this.stream?.getTracks().forEach((t) => t.stop());
    this.stream = undefined;
    this.key = undefined;
  }

  private async buildStartInfo(
    track: MediaStreamTrack | undefined,
    continuousFocusApplied: boolean,
  ): Promise<CameraStartInfo> {
    const s = (track?.getSettings?.() ?? {}) as MediaTrackSettings;
    let videoInputCount = 0;
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      videoInputCount = devices.filter((d) => d.kind === 'videoinput').length;
    } catch {
      /* enumerateDevices may reject — leave count 0 */
    }
    return {
      deviceLabel: track?.label ?? '',
      facingMode: (s.facingMode as string) ?? null,
      width: s.width ?? null,
      height: s.height ?? null,
      frameRate: s.frameRate ?? null,
      aspectRatio: s.aspectRatio ?? null,
      videoInputCount,
      capabilities: track
        ? describeCapabilities(track)
        : { focusMode: [], pointsOfInterest: false, torch: false, zoom: null, width: null, height: null },
      continuousFocusApplied,
    };
  }
}
